import React, { useState, useEffect, useRef } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  Box, 
  FormControl, 
  Select, 
  MenuItem, 
  Typography, 
  Tabs,
  Tab,
  Paper,
  CircularProgress,
  IconButton,
  InputLabel,
  Divider,
  useTheme,
  useMediaQuery,
  Button,
  Stack,
  Chip,
  AppBar,
  Toolbar
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { getSignalHistory, getOnuStatusHistory, getCurrentSignals, getSpeedHistoryByPort } from '../../services/api';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const PERIODS = [
  { value: '1day', label: '1 день' },
  { value: '7days', label: '7 дней' },
  { value: '1month', label: '1 месяц' },
  { value: '3months', label: '3 месяца' }
];

const SPEED_DAYS_OPTIONS = [
  { value: 1, label: '1 день' },
  { value: 7, label: '7 дней' },
  { value: 14, label: '14 дней' },
  { value: 30, label: '30 дней' }
];

const formatSignalValue = (value) => {
  if (!value) return -40;
  return value / 100;
};

const formatPortNumber = (portNumber) => {
  if (!portNumber) return '';
  const port = Math.floor((portNumber - 16779009) / 256) + 1;
  const subPort = ((portNumber - 16779009) % 256) + 1;
  return `${port}/${subPort}`;
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  // Вычитаем 7 часов для коррекции часового пояса
  date.setHours(date.getHours() - 7);
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

const formatSpeedTime = (isoString) => {
  if (!isoString) return '';
  // Сократим до "MM-DD HH:mm" (без TZ плясок — это чисто подпись на графике)
  const d = new Date(isoString);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${mm}-${dd} ${hh}:${mi}`;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <Box sx={{ 
      bgcolor: 'background.paper', 
      p: 1, 
      border: '1px solid #ccc',
      borderRadius: 1
    }}>
      <Typography variant="body2">{label}</Typography>
      {payload.map((entry, index) => (
        <Typography 
          key={index} 
          variant="body2" 
          sx={{ color: entry.color }}
        >
          {entry.name}: {entry.value === -0.4 ? 'Нет сигнала' : `${entry.value.toFixed(2)} dBm`}
        </Typography>
      ))}
    </Box>
  );
};

// Компонент для отображения вкладок
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
      style={{ height: '100%' }}
    >
      {value === index && (
        <Box sx={{ p: 1, height: '100%' }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Добавьте функцию для форматирования причин потери сигнала
const formatSignalLossReason = (reason) => {
  if (!reason) return 'Нет данных';
  
  switch (reason) {
    case 'dying-gasp':
      return 'Отключен по питанию';
    case 'reboot':
      return 'Перезагрузка';
    case 'LOS':
      return 'Проблема с волокном';
    default:
      return reason;
  }
};

// Обновите функцию форматирования данных статуса ONU
const formatOnuStatusData = (data) => {
  if (!Array.isArray(data) || data.length === 0) {
    console.log('Нет данных о статусе ONU или неверный формат данных');
    return [];
  }
  
  console.log(`Форматирование ${data.length} записей о статусе ONU`);
  console.log('Пример данных:', data[0]);
  
  return data.map(item => {
    // Используем timestamp как есть, без преобразования часовых поясов
    const timestampStr = String(item.timestamp);
    
    // Извлекаем только дату и время из строки ISO (если это ISO формат)
    // Например, из "2025-03-07T21:01:25.000Z" получаем "07.03.2025 21:01"
    let formattedDate = timestampStr;
    
    if (timestampStr.includes('T')) {
      const datePart = timestampStr.split('T')[0];
      const timePart = timestampStr.split('T')[1].split('.')[0];
      
      const [year, month, day] = datePart.split('-');
      const [hour, minute] = timePart.split(':');
      
      formattedDate = `${day}.${month}.${year} ${hour}:${minute}`;
    }
    
    // Форматируем причину потери сигнала
    const formattedReason = formatSignalLossReason(item.signal_loss_reason);
    
    return {
      ...item,
      timestamp: formattedDate,
      // Сохраняем оригинальную строку timestamp для сортировки
      originalTimestamp: timestampStr,
      // Преобразуем status в числовое значение для графика
      statusValue: item.status === 1 ? 1 : 0,
      // Добавляем текстовое описание статуса
      statusText: item.status === 1 ? 'UP' : 'DOWN',
      // Добавляем причину потери сигнала (если есть)
      reasonText: formattedReason,
      // Сохраняем оригинальную причину для цветового кодирования
      originalReason: item.signal_loss_reason
    };
  });
};

const SignalDetailView = ({ open, onClose, rowData }) => {
  const [period, setPeriod] = useState('1month');
  const [historyData, setHistoryData] = useState([]);
  const [onuStatusData, setOnuStatusData] = useState([]);
  const [loadingSignals, setLoadingSignals] = useState(false);
  const [loadingOnu, setLoadingOnu] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [error, setError] = useState(null);
  const [onuError, setOnuError] = useState(null);
  // Добавляем состояние для текущих показаний сигнала
  const [currentSignals, setCurrentSignals] = useState({
    signal_olt: 'Загрузка...',
    signal_onu: 'Загрузка...',
    loading: true,
    error: null
  });
  // Кулдаун кнопки обновления текущих показаний (секунд).
  const REFRESH_COOLDOWN_SEC = 5;
  // Добавляем состояние для отслеживания времени последнего обновления
  const [lastUpdateTime, setLastUpdateTime] = useState(0);
  // Добавляем состояние для отображения обратного отсчета
  const [countdown, setCountdown] = useState(0);
  // Храним interval в ref, чтобы не триггерить лишние ререндеры
  const countdownIntervalRef = useRef(null);
  // Добавляем состояние для инвертирования графика
  const [invertGraph, setInvertGraph] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Скорости (speedmonitor)
  const [speedDays, setSpeedDays] = useState(7);
  const [speedLoading, setSpeedLoading] = useState(false);
  const [speedError, setSpeedError] = useState(null);
  const [speedData, setSpeedData] = useState({
    router_id: null,
    history: [],
    pon_port: null,
    meta_missing: false,
    meta_schema_outdated: false,
    meta_source: null,
    message: null
  });

  const formatCurrentSignalLabel = (raw) => {
    if (raw == null || raw === '') return 'Нет данных';
    const n = Number(raw);
    if (!Number.isFinite(n)) return 'Нет данных';
    // В таблице/БД значения в сотых долях dBm; -40 = нет сигнала.
    if (n === -40) return 'Нет сигнала';
    // Если уже в dBm (история после formatSignalValue) — не делим ещё раз.
    const dbm = Math.abs(n) > 100 ? n / 100 : n;
    if (dbm === -0.4) return 'Нет сигнала';
    return `${dbm.toFixed(2)} dBm`;
  };

  // Обработчик изменения вкладки
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Функция для получения текущих показаний сигнала через SNMP/CDATA
  const fetchCurrentSignals = async (oltIp, portNumber, { seedFromRow = false } = {}) => {
    try {
      // Мгновенно показываем последнее известное из строки таблицы, live — фоном.
      if (seedFromRow && rowData) {
        setCurrentSignals({
          signal_olt: formatCurrentSignalLabel(rowData.signal_olt),
          signal_onu: formatCurrentSignalLabel(rowData.signal_onu),
          loading: false,
          error: null
        });
      } else {
        setCurrentSignals(prev => ({ ...prev, loading: true, error: null }));
      }

      const now = Date.now();
      setLastUpdateTime(now);
      startCountdown();

      try {
        const data = await getCurrentSignals(oltIp, portNumber);

        if (data.debug && data.debug.onuOutput && data.debug.onuOutput.includes('No Such Instance')) {
          setCurrentSignals({
            signal_olt: formatCurrentSignalLabel(data.signal_olt),
            signal_onu: 'ONU не отвечает',
            loading: false,
            error: null
          });
        } else {
          setCurrentSignals({
            signal_olt: formatCurrentSignalLabel(data.signal_olt),
            signal_onu: formatCurrentSignalLabel(data.signal_onu),
            loading: false,
            error: null
          });
        }
      } catch (apiError) {
        console.error('Ошибка при получении текущих показаний сигнала через API');

        if (historyData.length > 0) {
          const lastItem = historyData[historyData.length - 1];
          setCurrentSignals({
            signal_olt: formatCurrentSignalLabel(lastItem.signal_olt),
            signal_onu: formatCurrentSignalLabel(lastItem.signal_onu),
            loading: false,
            error: null
          });
        } else if (rowData && (rowData.signal_olt != null || rowData.signal_onu != null)) {
          // Оставляем seed из строки таблицы
          setCurrentSignals({
            signal_olt: formatCurrentSignalLabel(rowData.signal_olt),
            signal_onu: formatCurrentSignalLabel(rowData.signal_onu),
            loading: false,
            error: null
          });
        } else {
          setCurrentSignals({
            signal_olt: 'Нет данных',
            signal_onu: 'Нет данных',
            loading: false,
            error: 'Ошибка получения данных'
          });
        }
      }
    } catch (err) {
      console.error('Ошибка при получении текущих показаний сигнала');
      setCurrentSignals({
        signal_olt: 'Ошибка',
        signal_onu: 'Ошибка',
        loading: false,
        error: err.message
      });
    }
  };

  // Функция для запуска обратного отсчета
  const startCountdown = () => {
    // Очищаем предыдущий интервал, если он существует
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    
    // Устанавливаем начальное значение обратного отсчета
    setCountdown(REFRESH_COOLDOWN_SEC);
    
    // Создаем новый интервал
    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          // Если обратный отсчет закончился, очищаем интервал
          clearInterval(interval);
          countdownIntervalRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    countdownIntervalRef.current = interval;
  };

  // Очищаем интервал при закрытии/размонтировании диалога
  useEffect(() => {
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, []);

  // ВАЖНО: таймер нужен только для вкладки "История сигналов".
  // Иначе он дергает ререндер каждую секунду и Recharts Tooltip начинает мигать.
  useEffect(() => {
    if (!open) return;
    if (tabValue !== 0 && countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
      setCountdown(0);
    }
  }, [tabValue, open]);

  useEffect(() => {
    if (!open || !rowData) return;

    let cancelled = false;

    const fetchSignals = async () => {
      setLoadingSignals(true);
      setError(null);
      try {
        const data = await getSignalHistory(rowData.olt_ip, rowData.port_number, period);
        if (cancelled) return;
        if (Array.isArray(data)) {
          setHistoryData(data.map(item => ({
            ...item,
            signal_olt: formatSignalValue(item.signal_olt),
            signal_onu: formatSignalValue(item.signal_onu),
            date: formatDate(item.date)
          })));
        } else {
          console.error('Данные истории сигналов не являются массивом:', data);
          setHistoryData([]);
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Ошибка при загрузке истории сигналов:', err);
        setError(err.message);
        setHistoryData([]);
      } finally {
        if (!cancelled) setLoadingSignals(false);
      }
    };

    const fetchOnu = async () => {
      setLoadingOnu(true);
      setOnuError(null);
      try {
        const onuData = await getOnuStatusHistory(rowData.olt_ip, rowData.port_number, period);
        if (cancelled) return;
        if (Array.isArray(onuData) && onuData.length > 0) {
          setOnuStatusData(formatOnuStatusData(onuData));
        } else {
          setOnuStatusData([]);
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Ошибка при загрузке истории статуса ONU:', err);
        setOnuError(err.message);
        setOnuStatusData([]);
      } finally {
        if (!cancelled) setLoadingOnu(false);
      }
    };

    // Параллельно: график сигналов не ждёт onu-status и наоборот.
    fetchSignals();
    fetchOnu();
    fetchCurrentSignals(rowData.olt_ip, rowData.port_number, { seedFromRow: true });

    return () => { cancelled = true; };
  }, [rowData, period, open]);

  // Загрузка истории скоростей (отдельно от истории сигналов)
  useEffect(() => {
    const fetchSpeed = async () => {
      if (!open || !rowData?.olt_ip || !rowData?.port_number) return;

      setSpeedLoading(true);
      setSpeedError(null);
      try {
        const data = await getSpeedHistoryByPort(rowData.olt_ip, rowData.port_number, speedDays);
        setSpeedData({
          router_id: data?.router_id || null,
          history: Array.isArray(data?.history) ? data.history : [],
          pon_port: data?.pon_port ?? null,
          meta_missing: !!data?.meta_missing,
          meta_schema_outdated: !!data?.meta_schema_outdated,
          meta_source: data?.meta_source || null,
          message: data?.message || null
        });
      } catch (e) {
        setSpeedError(e?.message || 'Ошибка получения истории скоростей');
        setSpeedData({
          router_id: null,
          history: [],
          pon_port: null,
          meta_missing: false,
          meta_schema_outdated: false,
          meta_source: null,
          message: null
        });
      } finally {
        setSpeedLoading(false);
      }
    };

    fetchSpeed();
  }, [open, rowData, speedDays]);

  // Проверяем, можно ли обновить данные (прошёл ли кулдаун)
  const canUpdate = countdown === 0;

  // Функция для отображения подсказки для второго графика
  const CustomTooltipOnuStatus = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    
    const data = payload[0].payload;
    
    return (
      <Paper sx={{ p: 1, maxWidth: 300 }}>
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          {data.timestamp}
        </Typography>
        <Typography variant="body2">
          Статус: {data.statusText}
        </Typography>
        {data.status === 2 && data.reasonText && (
          <Typography variant="body2">
            Причина: {data.reasonText}
          </Typography>
        )}
        {data.description && (
          <Typography variant="body2" sx={{ mt: 1, fontSize: '0.8rem' }}>
            Описание: {data.description}
          </Typography>
        )}
      </Paper>
    );
  };

  // Функция для определения цвета точки в зависимости от причины потери сигнала
  const getPointColor = (entry) => {
    if (entry.status === 1) return '#4CAF50'; // Зеленый для UP
    
    // Разные цвета для разных причин потери сигнала
    switch (entry.signal_loss_reason) {
      case 'LOS':
        return '#F44336'; // Красный
      case 'reboot':
        return '#FFC107'; // Желтый
      case 'dying-gasp':
        return '#9C27B0'; // Фиолетовый
      default:
        return '#FF9800'; // Оранжевый для других причин
    }
  };

  // Исправление предупреждения о ключах в списке для компонента Line
  const renderDots = (props) => {
    const { cx, cy, payload, index } = props;
    if (payload.signal_loss_reason) {
      return (
        <circle 
          key={`dot-${index}`}
          cx={cx} 
          cy={cy} 
          r={4} 
          fill={getPointColor(payload)} 
          stroke="none" 
        />
      );
    }
    return null;
  };

  // Функция для переключения инвертирования графика
  const toggleGraphInversion = () => {
    setInvertGraph(!invertGraph);
  };

  // Мобильная версия заголовка
  const MobileHeader = () => (
    <AppBar position="static" color="transparent" elevation={0}>
      <Toolbar sx={{ px: 1 }}>
        <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="subtitle1" sx={{ ml: 1, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {formatPortNumber(rowData?.port_number)} - {rowData?.port_description || 'Нет описания'}
        </Typography>
      </Toolbar>
    </AppBar>
  );

  // Мобильная версия панели управления периодом и обновлением
  const MobileControls = () => (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <FormControl size="small" sx={{ minWidth: '50%' }}>
          <Select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            displayEmpty
            variant="outlined"
            sx={{ fontSize: '0.875rem' }}
          >
            <MenuItem value="1day">1 день</MenuItem>
            <MenuItem value="7days">7 дней</MenuItem>
            <MenuItem value="1month">1 месяц</MenuItem>
            <MenuItem value="3months">3 месяца</MenuItem>
          </Select>
        </FormControl>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton 
            size="small" 
            onClick={() => fetchCurrentSignals(rowData.olt_ip, rowData.port_number)}
            disabled={!canUpdate || currentSignals.loading}
            color="primary"
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
          
          {countdown > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mx: 1 }}>
              {countdown}с
            </Typography>
          )}
          
          <IconButton
            size="small"
            onClick={toggleGraphInversion}
            color={invertGraph ? "secondary" : "default"}
          >
            <SwapVertIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );

  // Мобильная версия отображения текущих сигналов
  const MobileCurrentSignals = () => (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
        <Chip 
          label={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="caption" sx={{ mr: 0.5 }}>OLT:</Typography>
              {currentSignals.loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CircularProgress size={12} sx={{ mr: 0.5 }} />
                  <Typography variant="caption">Загрузка...</Typography>
                </Box>
              ) : (
                <Typography variant="caption">{currentSignals.signal_olt}</Typography>
              )}
            </Box>
          }
          color="primary"
          variant="outlined"
          sx={{ flex: 1, height: 'auto', py: 0.5 }}
        />
        
        <Chip 
          label={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="caption" sx={{ mr: 0.5 }}>ONU:</Typography>
              {currentSignals.loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CircularProgress size={12} sx={{ mr: 0.5 }} />
                  <Typography variant="caption">Загрузка...</Typography>
                </Box>
              ) : (
                <Typography variant="caption">{currentSignals.signal_onu}</Typography>
              )}
            </Box>
          }
          color="secondary"
          variant="outlined"
          sx={{ flex: 1, height: 'auto', py: 0.5 }}
        />
      </Stack>
    </Box>
  );

  // Мобильная версия легенды для графика сигналов
  const MobileSignalLegend = () => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1, mb: 1 }}>
      <Chip 
        size="small"
        label="Сигнал OLT" 
        sx={{ bgcolor: '#8884d8', color: 'white', height: 24 }}
      />
      <Chip 
        size="small"
        label="Сигнал ONU" 
        sx={{ bgcolor: '#82ca9d', color: 'white', height: 24 }}
      />
    </Box>
  );

  // Мобильная версия легенды для графика статусов
  const MobileStatusLegend = () => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1, mb: 1 }}>
      <Chip 
        size="small"
        label="UP - активен" 
        sx={{ bgcolor: 'green', color: 'white', height: 24 }}
      />
      <Chip 
        size="small"
        label="DOWN - неактивен" 
        sx={{ bgcolor: 'red', color: 'white', height: 24 }}
      />
    </Box>
  );

  // Мобильная версия графика сигналов
  const MobileSignalChart = () => (
    <Box height="300px">
      <MobileSignalLegend />
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={historyData}
          margin={{ top: 5, right: 5, left: 0, bottom: 50 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="date" 
            tick={{ fontSize: 9 }}
            angle={-45}
            textAnchor="end"
            height={60}
            tickMargin={15}
            tickFormatter={(value) => {
              // Сокращаем формат даты для мобильных устройств
              return value.split(' ')[0]; // Показываем только дату без времени
            }}
          />
          <YAxis 
            tick={{ fontSize: 10 }}
            width={30}
            // Используем reversed для инвертирования оси
            reversed={invertGraph}
            // Устанавливаем фиксированный диапазон значений
            domain={[-40, -5]}
            // Устанавливаем фиксированные отметки
            ticks={[-40, -30, -20, -10]}
          />
          <Tooltip content={<CustomTooltip />} />
          <Line
            type="monotone"
            dataKey="signal_olt"
            name="Сигнал OLT"
            stroke="#8884d8"
            dot={false}
            strokeWidth={2}
          />
          <Line
            type="monotone"
            dataKey="signal_onu"
            name="Сигнал ONU"
            stroke="#82ca9d"
            dot={false}
            strokeWidth={2}
          />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );

  // Мобильная версия графика статусов
  const MobileStatusChart = () => (
    <Box height="300px">
      <MobileStatusLegend />
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={onuStatusData}
          margin={{ top: 5, right: 5, left: 0, bottom: 50 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="timestamp" 
            tick={{ fontSize: 9 }}
            angle={-45}
            textAnchor="end"
            height={60}
            tickMargin={15}
            tickFormatter={(value) => {
              // Сокращаем формат даты для мобильных устройств
              return value.split(' ')[0]; // Показываем только дату без времени
            }}
          />
          <YAxis 
            domain={[0, 1]}
            tick={{ fontSize: 10 }}
            ticks={[0, 1]}
            width={40}
            tickFormatter={(value) => {
              if (value === 0) return 'DOWN';
              if (value === 1) return 'UP';
              return '';
            }}
          />
          <Tooltip content={<CustomTooltipOnuStatus />} />
          <Line
            type="stepAfter"
            dataKey="statusValue"
            name="Статус порта"
            stroke="#ff7300"
            dot={renderDots}
            activeDot={{ r: 6 }}
            strokeWidth={2}
          />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );

  const SpeedContent = () => {
    const history = speedData.history || [];
    const latest = history.length ? history[history.length - 1] : null;
    const chartData = history.map(item => ({
      ...item,
      t: formatSpeedTime(item.started_at || item.finished_at),
      download_mbps: typeof item.download_mbps === 'number' ? item.download_mbps : null,
      upload_mbps: typeof item.upload_mbps === 'number' ? item.upload_mbps : null
    }));

    return (
      <Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Период</InputLabel>
            <Select
              value={speedDays}
              label="Период"
              onChange={(e) => setSpeedDays(parseInt(e.target.value, 10))}
            >
              {SPEED_DAYS_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Chip
            label={speedData.router_id ? `ROUTER_ID: ${speedData.router_id}` : 'ROUTER_ID: не привязан'}
            color={speedData.router_id ? 'success' : 'warning'}
            variant="outlined"
          />

          {latest && (
            <>
              <Chip
                label={`DL: ${latest.download_mbps?.toFixed?.(1) ?? '-'} Mbps`}
                color="primary"
                variant="outlined"
              />
              <Chip
                label={`UL: ${latest.upload_mbps?.toFixed?.(1) ?? '-'} Mbps`}
                color="secondary"
                variant="outlined"
              />
            </>
          )}
        </Box>

        {speedLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={300}>
            <CircularProgress />
          </Box>
        ) : speedError ? (
          <Box p={2} color="error.main">{speedError}</Box>
        ) : !speedData.router_id ? (
          <Paper sx={{ p: 2 }}>
            <Typography variant="body2">
              Для этого порта нет привязки router_id. Добавь запись в таблицу <code>pon_port_meta</code> и обнови.
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              olt_ip={rowData?.olt_ip}, port_number={rowData?.port_number}
            </Typography>
            {speedData.pon_port != null && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                pon_port={speedData.pon_port}
              </Typography>
            )}
            {speedData.meta_missing && (
              <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 1 }}>
                Таблица pon_port_meta не найдена (meta_missing=true). Примените SQL миграцию.
              </Typography>
            )}
            {speedData.meta_schema_outdated && (
              <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
                Схема pon_port_meta старая (meta_schema_outdated=true). Сервер попробует fallback, но лучше обновить схему.
              </Typography>
            )}
            {speedData.meta_source && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                meta_source={speedData.meta_source}
              </Typography>
            )}
            {speedData.message && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {speedData.message}
              </Typography>
            )}
          </Paper>
        ) : chartData.length === 0 ? (
          <Paper sx={{ p: 2 }}>
            <Typography variant="body2">Нет замеров за выбранный период.</Typography>
          </Paper>
        ) : (
          <Box height={350}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="t" angle={-45} textAnchor="end" height={60} tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => (typeof v === 'number' ? v.toFixed(1) : v)} />
                <Tooltip
                  formatter={(value) => {
                    if (typeof value === 'number') return value.toFixed(1);
                    return value;
                  }}
                />
                <Legend verticalAlign="top" height={24} />
                <Line
                  type="monotone"
                  dataKey="download_mbps"
                  name="Download (Mbps)"
                  stroke="#1976d2"
                  dot={false}
                  strokeWidth={2}
                  isAnimationActive={false}
                />
                <Line
                  type="monotone"
                  dataKey="upload_mbps"
                  name="Upload (Mbps)"
                  stroke="#9c27b0"
                  dot={false}
                  strokeWidth={2}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        )}
      </Box>
    );
  };

  // Мобильная версия всего диалога
  if (isMobile) {
    return (
      <Dialog 
        open={open} 
        onClose={onClose}
        fullScreen
        TransitionProps={{
          onExited: () => {
            // Сбрасываем состояние при закрытии диалога
            setTabValue(0);
            setHistoryData([]);
            setOnuStatusData([]);
          }
        }}
      >
        <MobileHeader />
        
        <DialogContent sx={{ p: 1, pt: 0 }}>
          <MobileControls />
          <MobileCurrentSignals />
          
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            variant="fullWidth"
            sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="История сигналов" />
            <Tab label="История статуса" />
            <Tab label="Скорость" />
          </Tabs>
          
          <TabPanel value={tabValue} index={0}>
            {loadingSignals ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="300px">
                <CircularProgress />
              </Box>
            ) : error ? (
              <Box p={2} color="error.main">{error}</Box>
            ) : (
              <MobileSignalChart />
            )}
          </TabPanel>
          
          <TabPanel value={tabValue} index={1}>
            {loadingOnu ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="300px">
                <CircularProgress />
              </Box>
            ) : onuError ? (
              <Box p={2} color="error.main">{onuError}</Box>
            ) : (
              <MobileStatusChart />
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <SpeedContent />
          </TabPanel>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ pr: 2 }}>
            Детальная информация о сигнале
          </Typography>
          <IconButton onClick={onClose} size="large">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ 
          mb: 2, 
          display: 'flex', 
          flexDirection: 'row',
          justifyContent: 'space-between', 
          alignItems: 'center', 
        }}>
          <Typography variant="h6" sx={{ mb: { xs: 1, md: 0 } }}>
            {formatPortNumber(rowData?.port_number)} - {rowData?.port_description || 'Нет описания'}
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'row',
            gap: 2,
            width: 'auto'
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              width: 'auto'
            }}>
              <Typography variant="body2">
                Период:
            </Typography>
              <Select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                size="small"
                sx={{ 
                  minWidth: 120,
                }}
              >
                <MenuItem value="1day">1 день</MenuItem>
                <MenuItem value="7days">7 дней</MenuItem>
                <MenuItem value="1month">1 месяц</MenuItem>
                <MenuItem value="3months">3 месяца</MenuItem>
              </Select>
            </Box>
          </Box>
        </Box>
        
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          sx={{ mb: 2 }}
          variant="standard"
        >
          <Tab label="История сигналов" />
          <Tab label="История статуса" />
          <Tab label="Скорость" />
        </Tabs>
        
        <TabPanel value={tabValue} index={0}>
          {loadingSignals ? (
            <Box display="flex" justifyContent="center" alignItems="center" height={450}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box p={2} color="error.main">{error}</Box>
          ) : (
            <Box height={450}>
              <Box mt={2} mb={2} display="flex" flexWrap="wrap" gap={4} justifyContent="center" alignItems="center">
                <Box display="flex" alignItems="center">
                  <Box component="span" sx={{ display: 'inline-block', width: 12, height: 12, bgcolor: '#8884d8', mr: 1 }} />
                  <Typography variant="body2">Сигнал OLT</Typography>
                </Box>
                <Box display="flex" alignItems="center">
                  <Box component="span" sx={{ display: 'inline-block', width: 12, height: 12, bgcolor: '#82ca9d', mr: 1 }} />
                  <Typography variant="body2">Сигнал ONU</Typography>
                </Box>
                
                <Divider orientation="vertical" flexItem sx={{ height: 24 }} />
                
                <Box display="flex" alignItems="center">
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mr: 1 }}>
                    Текущий сигнал от ONU:
                  </Typography>
                  <Typography variant="body2" color={currentSignals.loading ? 'text.secondary' : 'text.primary'}>
                    {currentSignals.loading ? (
                      <Box component="span" display="inline-flex" alignItems="center">
                        <CircularProgress size={14} sx={{ mr: 1 }} />
                        Загрузка...
                      </Box>
                    ) : currentSignals.error ? (
                      <Box component="span" color="error.main">Ошибка</Box>
                    ) : (
                      currentSignals.signal_onu
                    )}
                  </Typography>
                </Box>
                
                <Box display="flex" alignItems="center">
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mr: 1 }}>
                    Текущий сигнал от OLT:
                  </Typography>
                  <Typography variant="body2" color={currentSignals.loading ? 'text.secondary' : 'text.primary'}>
                    {currentSignals.loading ? (
                      <Box component="span" display="inline-flex" alignItems="center">
                        <CircularProgress size={14} sx={{ mr: 1 }} />
                        Загрузка...
                      </Box>
                    ) : currentSignals.error ? (
                      <Box component="span" color="error.main">Ошибка</Box>
                    ) : (
                      currentSignals.signal_olt
                    )}
          </Typography>
        </Box>
                
                <Box display="flex" alignItems="center" gap={2}>
                  <IconButton 
                    size="small" 
                    onClick={() => fetchCurrentSignals(rowData.olt_ip, rowData.port_number)}
                    disabled={!canUpdate || currentSignals.loading}
                    title={canUpdate ? "Обновить текущие показания" : `Подождите ${countdown} сек.`}
                    color="primary"
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                  {countdown > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                      {countdown} сек.
                    </Typography>
                  )}
                  
                  {/* Кнопка для инвертирования графика */}
                  <IconButton
                    size="small"
                    onClick={toggleGraphInversion}
                    title={invertGraph ? "Вернуть обычный вид" : "Инвертировать график"}
                    color={invertGraph ? "secondary" : "default"}
                  >
                    <SwapVertIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
              
              <ResponsiveContainer width="100%" height="85%">
            <LineChart
              data={historyData}
                  margin={{ top: 5, right: 30, left: 20, bottom: 70 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                    tick={{ fontSize: 11 }}
                angle={-45}
                textAnchor="end"
                    height={70}
                    tickMargin={20}
              />
              <YAxis 
                    tick={{ fontSize: 12 }}
                label={{ 
                  value: 'dBm', 
                  angle: -90, 
                      position: 'insideLeft',
                      style: { textAnchor: 'middle' }
                    }}
                    // Используем reversed для инвертирования оси
                    reversed={invertGraph}
                    // Устанавливаем фиксированный диапазон значений
                    domain={[-40, -5]}
                    // Устанавливаем фиксированные отметки
                    ticks={[-40, -35, -30, -25, -20, -15, -10, -5]}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line 
                type="monotone" 
                dataKey="signal_olt" 
                name="Сигнал OLT"
                stroke="#8884d8" 
                dot={false}
              />
              <Line 
                type="monotone" 
                dataKey="signal_onu" 
                name="Сигнал ONU"
                stroke="#82ca9d" 
                dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          )}
        </TabPanel>
        
        <TabPanel value={tabValue} index={1}>
          {loadingOnu ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="450px">
              <CircularProgress />
            </Box>
          ) : onuError ? (
            <Box p={2} color="error.main">{onuError}</Box>
          ) : (
            <Box height={450}>
              <Box mt={2} mb={2} display="flex" flexWrap="wrap" gap={2} justifyContent="center">
                <Box display="flex" alignItems="center">
                  <Box component="span" sx={{ display: 'inline-block', width: 12, height: 12, bgcolor: 'green', mr: 1 }} />
                  <Typography variant="body2">UP - порт активен</Typography>
                </Box>
                <Box display="flex" alignItems="center">
                  <Box component="span" sx={{ display: 'inline-block', width: 12, height: 12, bgcolor: 'red', mr: 1 }} />
                  <Typography variant="body2">DOWN - порт неактивен</Typography>
                </Box>
                <Box display="flex" alignItems="center">
                  <Box component="span" sx={{ display: 'inline-block', width: 12, height: 12, bgcolor: 'orange', mr: 1 }} />
                  <Typography variant="body2">Отключен по питанию</Typography>
                </Box>
                <Box display="flex" alignItems="center">
                  <Box component="span" sx={{ display: 'inline-block', width: 12, height: 12, bgcolor: 'purple', mr: 1 }} />
                  <Typography variant="body2">Перезагрузка</Typography>
                </Box>
                <Box display="flex" alignItems="center">
                  <Box component="span" sx={{ display: 'inline-block', width: 12, height: 12, bgcolor: 'blue', mr: 1 }} />
                  <Typography variant="body2">Проблема с волокном</Typography>
                </Box>
              </Box>
              
              <ResponsiveContainer width="100%" height="85%">
                <LineChart
                  data={onuStatusData}
                  margin={{ top: 5, right: 30, left: 20, bottom: 70 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="timestamp" 
                    tick={{ fontSize: 11 }}
                    angle={-45}
                    textAnchor="end"
                    height={70}
                    tickMargin={20}
                  />
                  <YAxis 
                    domain={[0, 1]}
                    tick={{ fontSize: 12 }}
                    ticks={[0, 1]}
                    tickFormatter={(value) => {
                      if (value === 0) return 'DOWN';
                      if (value === 1) return 'UP';
                      return '';
                    }}
                  />
                  <Tooltip content={<CustomTooltipOnuStatus />} />
                  <Line
                    type="stepAfter"
                    dataKey="statusValue"
                    name="Статус порта"
                    stroke="#ff7300"
                    dot={renderDots}
                    activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <SpeedContent />
        </TabPanel>
      </DialogContent>
    </Dialog>
  );
};

export default SignalDetailView; 